export const ADAPTIVE_CARD_CONTENT_TYPE = 'application/vnd.microsoft.card.adaptive';

/**
 * A button offered with an agent message. The payload is opaque and is posted back as the value of a message activity.
 * kind 'retry' starts a new conversation and resends the user's last message; 'dismiss' hides the buttons without contacting the agent.
 */
export interface ChatAction {
  label: string;
  primary: boolean;
  kind?: 'retry' | 'dismiss';
  payload: unknown;
}

export interface ParsedAdaptiveCard {
  text: string;
  actions: ChatAction[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Copilot Studio's connection card submits { action: 'Retry' | 'Cancel' }, which the agent does not handle well as a bare value.
function kindOf(data: unknown): ChatAction['kind'] {
  const name = isRecord(data) ? data['action'] : undefined;
  return name === 'Retry' ? 'retry' : name === 'Cancel' ? 'dismiss' : undefined;
}

/** Reduces an Adaptive Card to its text blocks and Action.Submit buttons; everything else is ignored. */
export function parseAdaptiveCard(card: unknown): ParsedAdaptiveCard {
  const texts: string[] = [];
  const actions: ChatAction[] = [];

  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!isRecord(node)) {
      return;
    }

    if (node['type'] === 'TextBlock' && typeof node['text'] === 'string') {
      texts.push(node['text']);
    } else if (node['type'] === 'Action.Submit' && typeof node['title'] === 'string') {
      const kind = kindOf(node['data']);
      actions.push({ label: node['title'], primary: node['style'] === 'positive', payload: node['data'], ...(kind ? { kind } : {}) });
    }

    for (const key of ['body', 'items', 'columns', 'actions']) {
      visit(node[key]);
    }
  };

  visit(card);
  return { text: texts.join('\n\n'), actions };
}
